import { useState, useEffect } from 'react';
import { useFilterStore } from '../store/filterStore';
import { layoutApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { BookmarkCheck, Trash2, Play, Calendar, BarChart3, LineChart, Table, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const Layouts = () => {
  const navigate = useNavigate();
  const { loadLayout } = useFilterStore();
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState(null);

  const fetchLayouts = async () => {
    try {
      const response = await layoutApi.getAll();
      setLayouts(response.data);
    } catch (error) {
      toast.error('Failed to load saved layouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLayouts();
  }, []);

  const handleApplyLayout = (layout) => {
    loadLayout(layout);
    toast.success(`Layout "${layout.name}" applied`);
    navigate('/');
  };

  const handleDelete = async () => {
    if (!selectedLayout) return;
    
    try {
      await layoutApi.delete(selectedLayout.id);
      toast.success('Layout deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedLayout(null);
      fetchLayouts();
    } catch (error) {
      toast.error('Failed to delete layout');
    }
  };

  const openDeleteDialog = (layout, e) => {
    e.stopPropagation();
    setSelectedLayout(layout);
    setDeleteDialogOpen(true);
  };

  const getViewIcon = (viewType, chartType) => {
    if (viewType === 'table') return Table;
    return chartType === 'bar' ? BarChart3 : LineChart;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-serif text-2xl md:text-3xl font-bold text-coffee-espresso">
          Saved Layouts
        </h1>
        <p className="text-muted-foreground mt-1">
          Quickly apply your favourite dashboard configurations
        </p>
      </div>

      {/* Layouts Grid */}
      {layouts.length === 0 ? (
        <Card className="py-12">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <BookmarkCheck className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-xl font-semibold mb-2">No Saved Layouts</h3>
            <p className="text-muted-foreground max-w-md">
              You haven't saved any layouts yet. Go to the Dashboard, set your preferred filters and view options, 
              then click "Save Layout" to save your configuration.
            </p>
            <Button className="mt-6 rounded-xl" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {layouts.map((layout) => {
            const ViewIcon = getViewIcon(layout.view_type, layout.chart_type);
            const hasFilters = layout.filters && Object.values(layout.filters).some(
              (v) => v && (Array.isArray(v) ? v.length > 0 : true)
            );

            return (
              <Card
                key={layout.id}
                className="dashboard-card cursor-pointer group"
                onClick={() => handleApplyLayout(layout)}
                data-testid={`layout-card-${layout.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                        <ViewIcon className="w-5 h-5 text-coffee-espresso" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{layout.name}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {format(parseISO(layout.created_at), 'MMM d, yyyy')}
                        </CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => openDeleteDialog(layout, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                      data-testid={`delete-layout-${layout.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="rounded-full">
                      {layout.view_type === 'table' ? 'Table' : `${layout.chart_type} Chart`}
                    </Badge>
                    <Badge variant="secondary" className="rounded-full capitalize">
                      {layout.group_by}
                    </Badge>
                    <Badge variant="secondary" className="rounded-full capitalize">
                      {layout.report_type}
                    </Badge>
                    {layout.compare_previous && (
                      <Badge variant="outline" className="rounded-full">
                        Compare
                      </Badge>
                    )}
                    {hasFilters && (
                      <Badge variant="outline" className="rounded-full">
                        Filters Applied
                      </Badge>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-border">
                    <Button
                      variant="ghost"
                      className="w-full justify-center gap-2 text-accent hover:text-accent"
                      onClick={() => handleApplyLayout(layout)}
                    >
                      <Play className="w-4 h-4" />
                      Apply Layout
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Layout</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedLayout?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-layout"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Layouts;
